import { useState, type FC } from 'react';
import { Bell, Check, CheckCheck, Calendar, CreditCard, AlertCircle } from 'lucide-react';
import {
  useNotifications,
  useUnreadNotificationCount,
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
} from '../../../hooks/useNotifications';
import type { Notification, NotificationType } from '../../../types/notification.types';
import { cn } from '../../../utils/cn';

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'BOOKING_CONFIRMED':
    case 'BOOKING_CANCELLED':
    case 'BOOKING_REMINDER':
    case 'BOOKING_RESCHEDULED':
      return Calendar;
    case 'PAYMENT_RECEIVED':
    case 'PAYMENT_FAILED':
      return CreditCard;
    default:
      return Bell;
  }
};

const getNotificationColor = (type: NotificationType): string => {
  switch (type) {
    case 'BOOKING_CONFIRMED':
    case 'PAYMENT_RECEIVED':
      return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30';
    case 'BOOKING_CANCELLED':
    case 'PAYMENT_FAILED':
      return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30';
    case 'BOOKING_REMINDER':
      return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30';
    case 'BOOKING_RESCHEDULED':
      return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30';
    default:
      return 'text-gray-600 dark:text-slate-400 bg-gray-100 dark:bg-slate-700';
  }
};

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

interface NotificationItemProps {
  notification: Notification;
  onMarkRead: (id: string) => void;
  isMarking: boolean;
}

const NotificationItem: FC<NotificationItemProps> = ({ notification, onMarkRead, isMarking }) => {
  const Icon = getNotificationIcon(notification.type);
  const colorClass = getNotificationColor(notification.type);

  return (
    <div
      className={cn(
        'p-4 rounded-lg border transition-all',
        notification.isRead ? 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
      )}
    >
      <div className="flex items-start gap-3">
        <div className={cn('p-2 rounded-full', colorClass)}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className="font-medium text-gray-900 dark:text-slate-100 text-sm">{notification.title}</h4>
            <span className="text-xs text-gray-500 dark:text-slate-400 whitespace-nowrap">
              {formatTimeAgo(notification.createdAt)}
            </span>
          </div>
          <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">{notification.message}</p>
        </div>
        {!notification.isRead && (
          <button
            onClick={() => onMarkRead(notification.id)}
            disabled={isMarking}
            className="p-1 text-gray-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            title="Mark as read"
          >
            <Check className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export const NotificationsSection: FC = () => {
  const [page, setPage] = useState(1);
  const { data: notificationsData, isLoading, error } = useNotifications(page);
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();

  const notifications = notificationsData?.notifications ?? [];
  const total = notificationsData?.total ?? 0;
  const hasMore = page * 20 < total;

  if (isLoading && notifications.length === 0) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-gray-200 dark:bg-slate-700 rounded animate-pulse w-48" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 bg-gray-200 dark:bg-slate-700 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
        <p className="text-red-800 dark:text-red-300">Failed to load notifications. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100">Notifications</h2>
          <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={() => markAllAsRead.mutate()}
            disabled={markAllAsRead.isPending}
            className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
          >
            <CheckCheck className="w-4 h-4" />
            Mark all as read
          </button>
        )}
      </div>

      {/* Notifications List */}
      {notifications.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
          <Bell className="w-12 h-12 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100">No notifications</h3>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
            You're all caught up! Check back later for updates.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkRead={(id) => markAsRead.mutate(id)}
              isMarking={markAsRead.isPending}
            />
          ))}
        </div>
      )}

      {/* Load More */}
      {hasMore && (
        <div className="text-center">
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={isLoading}
            className="px-4 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
          >
            {isLoading ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationsSection;
